import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'use-intl'
import type { EnglishMessages } from '@financialharmony/i18n/messageTypes'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Upload, PackageOpen } from 'lucide-react'
import { journalEntryService } from '../../services/journalEntryService'
import { FiscalYearApiClient } from '../../clients/fiscalYearApiClient'
import { JournalEntryDrawer } from './JournalEntryDrawer'
import { SieImportDialog } from './SieImportDialog'
import { DocumentExportDialog } from './DocumentExportDialog'
import { useJournalSourceLabel } from './useJournalLabels'
import type { JournalEntryWithSource } from '../../clients/journalEntryApiClient'
import type { FiscalYear } from '../../types/fiscalYear'

const SOURCE_CHIP_CLASS: Record<string, string> = {
  payable: 'bg-blue-100 text-blue-800',
  receivable: 'bg-green-100 text-green-800',
  sie_import: 'bg-primary/10 text-primary',
  expense: 'bg-purple-100 text-purple-800',
  receipt: 'bg-teal-100 text-teal-800',
  sales_report: 'bg-yellow-100 text-yellow-800',
  auto_created: 'bg-surface-inset text-muted-foreground',
  manual: 'bg-surface-inset text-muted-foreground',
  unknown: 'bg-surface-inset text-muted-foreground',
}

const SOURCE_FILTERS = ['all', 'payable', 'receivable', 'sie_import', 'expense', 'manual'] as const satisfies readonly (keyof EnglishMessages['consultantBooks']['journal']['filters'])[]

// Harmony's own number is the label: it is unique per company, whereas each imported fiscal
// year's SIE file restarts its own series numbering from 1.
function verLabel(row: JournalEntryWithSource): string {
  return `#${row.entry.journal_number}`
}

function importedAs(row: JournalEntryWithSource): string | null {
  if (row.originalSeries && row.originalNumber != null) {
    return `as imported: ${row.originalSeries}${row.originalNumber}`
  }
  return null
}

function fyLabel(fy: FiscalYear): string {
  return `${fy.endDate.slice(0, 4)}`
}

// Booking date = regDate (SIE registration date) or created_at (for suggestion-created entries).
// Shown for reference only; the list is ordered by transaction date (entry.date) on the server.
function bookingDate(row: JournalEntryWithSource): string {
  return row.regDate ?? row.entry.created_at?.slice(0, 10) ?? row.entry.date
}

const PAGE_SIZE = 50

export function JournalPage() {
  const t = useTranslations('consultantBooks.journal')
  const sourceLabel = useJournalSourceLabel()
  const [selectedEntry, setSelectedEntry] = useState<JournalEntryWithSource | null>(null)
  const [sourceFilter, setSourceFilter] = useState<string | undefined>()
  const [seriesFilter, setSeriesFilter] = useState('')
  const [importOpen, setImportOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [selectedFyId, setSelectedFyId] = useState<string | undefined>()
  const [page, setPage] = useState(1)
  const queryClient = useQueryClient()

  const { data: fyData } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn: () => FiscalYearApiClient.getFiscalYears(),
    select: (d) => {
      const sorted = [...d.items].sort((a, b) => b.startDate.localeCompare(a.startDate))
      return sorted
    },
  })

  // Default to the first open fiscal year once loaded
  const fiscalYears = fyData ?? []
  const activeFyId = selectedFyId ?? fiscalYears.find(fy => fy.status === 'open')?.id ?? fiscalYears[0]?.id

  const { data, isLoading } = useQuery({
    queryKey: ['journal-entries-with-source', activeFyId, sourceFilter, seriesFilter, page],
    queryFn: () => journalEntryService.getJournalEntriesWithSource({
      fiscalYearId: activeFyId,
      sourceType: sourceFilter,
      series: seriesFilter || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    enabled: !!activeFyId,
  })

  // FIN-769 PR2: /bookkeeping/journal?entry=<id> opens that entry, whichever year or page holds it.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedEntryId = searchParams.get('entry')
  const { data: linkedData, isError: linkedFailed } = useQuery({
    queryKey: ['journal-entry-linked', linkedEntryId],
    queryFn: () => journalEntryService.getJournalEntriesWithSource({ entryId: linkedEntryId! }),
    enabled: !!linkedEntryId,
  })
  const linkedEntry = linkedEntryId ? linkedData?.data[0] ?? null : null
  const linkedNotFound = !!linkedEntryId && !!linkedData && !linkedEntry
  const openEntry = selectedEntry ?? linkedEntry

  const closeEntry = () => {
    setSelectedEntry(null)
    if (searchParams.has('entry')) {
      const next = new URLSearchParams(searchParams)
      next.delete('entry')
      setSearchParams(next, { replace: true })
    }
  }

  const rows = data?.data ?? []
  const totalItems = data?.totalItems ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setExportOpen(true)} disabled={!activeFyId}>
            <PackageOpen className="h-4 w-4 mr-1.5" />
            {t('exportButton')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-1.5" />
            {t('importButton')}
          </Button>
        </div>
      </div>

      {(linkedFailed || linkedNotFound) && (
        <Alert variant={linkedFailed ? 'destructive' : 'default'}>
          <AlertDescription>
            {t(linkedFailed ? 'linkedFailed' : 'linkedNotFound')}
          </AlertDescription>
        </Alert>
      )}

      {/* Fiscal year tabs */}
      {fiscalYears.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {fiscalYears.map(fy => (
            <button
              key={fy.id}
              onClick={() => { setSelectedFyId(fy.id); setPage(1) }}
              className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                activeFyId === fy.id
                  ? 'bg-primary text-white border-primary'
                  : 'border-border text-muted-foreground hover:bg-surface-inset'
              }`}
            >
              {fyLabel(fy)}
              {fy.status === 'open' && (
                <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-success align-middle" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Source type filters */}
      <div className="flex gap-2 flex-wrap">
        {SOURCE_FILTERS.map(filter => {
          const type = filter === 'all' ? '' : filter
          return (
          <button
            key={filter}
            onClick={() => { setSourceFilter(type || undefined); setPage(1) }}
            className={`text-sm px-3 py-1 rounded-full border transition-colors ${
              sourceFilter === (type || undefined)
                ? 'bg-foreground text-background border-foreground'
                : 'border-border text-muted-foreground hover:bg-surface-inset'
            }`}
          >
            {t(`filters.${filter}`)}
          </button>
          )
        })}
        <input
          type="text"
          aria-label={t('filters.seriesLabel')}
          placeholder={t('filters.seriesPlaceholder')}
          maxLength={4}
          value={seriesFilter}
          onChange={e => { setSeriesFilter(e.target.value.trim().toUpperCase()); setPage(1) }}
          className="text-sm px-3 py-1 w-36 rounded-full border border-border bg-background text-foreground placeholder:text-muted-foreground"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {!activeFyId ? (
            <div className="px-4 py-8 text-center text-muted-foreground text-sm">
              {t('noFiscalYears')}
            </div>
          ) : isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium w-32">{t('columns.ver')}</th>
                  <th className="px-4 py-3 font-medium w-28">{t('columns.date')}</th>
                  <th className="px-4 py-3 font-medium w-28 hidden md:table-cell">{t('columns.booked')}</th>
                  <th className="px-4 py-3 font-medium">{t('columns.description')}</th>
                  <th className="px-4 py-3 font-medium w-48 hidden lg:table-cell">{t('columns.accounts')}</th>
                  <th className="px-4 py-3 font-medium w-36">{t('columns.source')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      {t('empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map(row => (
                    <tr
                      key={row.entry.id}
                      onClick={() => setSelectedEntry(row)}
                      className="border-b border-border last:border-0 hover:bg-surface-inset cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-muted-foreground tabular-nums font-mono text-xs">
                        {verLabel(row)}
                        {importedAs(row) && (
                          <div className="font-sans text-[10px]">{importedAs(row)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{row.entry.date}</td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground hidden md:table-cell">
                        {bookingDate(row) !== row.entry.date ? bookingDate(row) : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium truncate max-w-xs">{row.entry.title}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {row.accounts.slice(0, 4).map(acc => (
                            <span
                              key={acc}
                              className="inline-block px-1.5 py-0.5 rounded bg-surface-inset text-[10px] font-mono text-muted-foreground border border-border"
                            >
                              {acc}
                            </span>
                          ))}
                          {row.accounts.length > 4 && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-surface-inset text-[10px] text-muted-foreground border border-border">
                              +{row.accounts.length - 4}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            SOURCE_CHIP_CLASS[row.sourceType] ?? SOURCE_CHIP_CLASS.unknown
                          }`}
                        >
                          {sourceLabel(row.sourceType)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalItems > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {t('pagination.range', { from: String((page - 1) * PAGE_SIZE + 1), to: String(Math.min(page * PAGE_SIZE, totalItems)), total: String(totalItems) })}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border border-border hover:bg-surface-inset disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('pagination.prev')}
            </button>
            <span className="px-3 py-1 tabular-nums">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 rounded border border-border hover:bg-surface-inset disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('pagination.next')}
            </button>
          </div>
        </div>
      )}

      {openEntry && (
        <JournalEntryDrawer
          entry={openEntry}
          onClose={closeEntry}
        />
      )}

      <SieImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: ['journal-entries-with-source'] })
          queryClient.invalidateQueries({ queryKey: ['fiscal-years'] })
        }}
      />

      <DocumentExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        fiscalYearId={activeFyId}
        fiscalYearLabel={fiscalYears.find(fy => fy.id === activeFyId) ? fyLabel(fiscalYears.find(fy => fy.id === activeFyId)!) : ''}
      />
    </div>
  )
}
